// =============================================================================
// Core Domain Types - Users, Organizations, Sales, Client Management
// =============================================================================
// PURPOSE: Central type definitions for the entire application. Defines data shapes
// for transactions, users, organizations, roles, and all domain entities.
//
// ARCHITECTURE:
// - Transaction types: SalesTransaction (runtime camelCase) vs DB schema (snake_case)
// - *_readable types: include JOIN fields from database views
// - Role hierarchy: exec > client > manager > closer/setter > dialer > viewer
// - Multi-tenant: organization_id on every entity
//
// KEY DECISIONS:
// - SalesTransaction uses camelCase: transformed in DataContext for React conventions
// - Status values: paid_in_full/closed_payment_plan = wins, %lost% = losses, %no_show% = no-shows
// - GHL prefix: renamed from instagram_* to support multi-channel (SMS, WhatsApp, etc.)
// - Backward compatibility: InstagramConversation aliases maintained for existing code
// =============================================================================

// --- Applicant & Client Status ---

export enum ApplicantStatus {
  Pending = "Pending",
  Interviewing = "Interviewing",
  Offered = "Offered",
  Rejected = "Rejected",
  Hired = "Hired",
}

export enum ClientStatus {
  Pending = "Pending",
  Active = "Active",
  OnHold = "On Hold",
  Archived = "Archived",
}

// --- Hiring & Clients ---

export interface Applicant {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: "Closer" | "Setter";
  experience: string;
  linkedIn?: string;
  status: ApplicantStatus;
  applicationDate: string;
}

export interface Client {
  id: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  currentRevenue: number;
  targetRevenue: number;
  status: ClientStatus;
  applicationDate: string;
  assignedTeam?: string[];
}

// --- Performance & Metrics ---

export interface CloserMetrics {
  callsBooked: number;
  callsTaken: number;
  dealsClosed: number;
  cashCollected: number;
  revenueClosed: number;
}

export interface SetterMetrics {
  callsBooked: number;
  callsTaken: number;
}

export interface DailyPerformanceLog {
  date: string;
  clientId: string;
  memberId: string;
  role: "Closer" | "Setter";
  metrics: CloserMetrics | SetterMetrics;
}

export interface SharedResource {
  id: string;
  name: string;
  type: "link" | "pdf" | "document";
  url: string;
  sharedDate: string;
  sharedBy: string;
  description: string;
}

// --- Sales Transactions (Runtime Data Structure) ---
// IMPORTANT: This interface matches the TRANSFORMED data from DataContext (lines 339-365),
// NOT the raw database schema. The raw DB uses snake_case but DataContext transforms to camelCase.

export interface SalesTransaction {
  // Core fields
  id: string;
  organization_id: string;
  organization_name: string | null; // Added by JOIN from organizations table (nullable in views)

  // Event metadata (camelCase after transformation)
  eventType: string; // Transformed from: event_type
  timestamp: string; // Transformed from: event_date || created_at
  dateOfCall: string | null; // Copied from: event_date (alternative reference)

  // Lead info (derived during transformation)
  clientName: string | null; // Derived from: leads.name || email
  leadEmail: string | null; // Derived from: leads.email || email

  // People IDs (unchanged)
  closerId: string | null;
  setterId: string | null;

  // People names (added by JOINs)
  closerName: string | null; // From JOIN: closer.full_name
  setterName: string | null; // From JOIN: setter.full_name

  // Outcome (renamed during transformation)
  callOutcome: string; // Transformed from: status

  // Financial (camelCase after transformation)
  cashCollected: number; // Transformed from: cash_collected
  revenueClosed: number; // Transformed from: revenue_closed
  programSold: string | null; // Transformed from: sales_program_sold

  // Payment tracking (camelCase after transformation)
  nextPaymentDate: string | null; // Transformed from: sales_next_payment
  moneyLeft: number; // Calculated field (always 0 in current impl)

  // QA fields (extracted from custom_data JSONB during transformation)
  ownershipLabel?: string | null; // From: custom_data.ownership_label (setter ownership rule 2026-10-06)
  filedAt?: string | null; // Raw event_date — when the outcome was filed. Gates the ownership label (from 2026-09-01)
  warmthLevel?: string | null; // From: custom_data.warmth_level — how long lead has known the brand
  fathomLink?: string | null; // From: custom_data.fathom_link
  qaScore?: number | null; // From: custom_data.quality_of_lead
  qaNotes?: string | null; // From: custom_data.outcome_notes
  setterQaScore?: number | null; // From: custom_data.setter_qa_score (if present)
  avgConversationDepth?: number | null; // From: custom_data (if present)
  avgResponseTimeSeconds?: number | null; // From: custom_data (if present)

  // Lead History (Transaction Log warmth-column right-click swap). Populated only when
  // usePaginatedTransactions is called with enrichLeadHistory:true, via get_lead_history_for_txns.
  // Optional: TransactionContext.tsx builds SalesTransaction too and omits these.
  firstTouchAt?: string | null; // From RPC: lead_funnel_source.first_touch_at (any-channel first touch)
  txnCount?: number | null; // From RPC: org-wide count of transactions rows for this lead
  country?: string | null; // From RPC get_lead_country_for_txns: lead_country_from_phone(leads.phone), 'Unknown' bucket for no-'+' phones
}

// --- Leaderboard & Team ---

export interface LeaderboardEntry {
  rep_id: string;
  rep_email: string;
  rep_name: string;
  date: string;
  organization_id: string;
  organization_name: string;
  pod_id: string;
  pod_name: string;
  pod_color: string;
  sector: string;
  cc_total: number;
  calls_taken: number;
  calls_booked: number;
  shows: number;
  no_shows: number;
  wins: number;
  losses: number;
  reschedules: number;
  daily_dials: number;
  show_rate: number;
  close_rate: number;
  connect_to_book_rate: number;
  cpc: number;
  rank: number;
  rank_score: number | null;
  points_earned: number;
  // Forward-looking SCO orphan pre-calls per /metrics canonical Show Rate (May 1, 2026 P0a).
  // Surfaced from daily_leaderboard_readable; required for show_rate denominator on aggregate views.
  orphan_precalls?: number;
  // Provisional close-rate term per /metrics doctrine (2026-06-19). pending_close is a PROVISIONAL
  // win — close-rate NUMERATOR ONLY (wins + pending_closes); never added to displayed wins/deals/AOV/cash.
  // Surfaced from daily_leaderboard_readable so client-side close-rate recomputes stay provisional.
  pending_closes?: number;
  // P0-LBTOTAL (2026-07-03): prestige-total components per Nathaniel's SSOT
  // (REF_nathaniel_leaderboard_prestige_kpi_source.md:370-374) — winner =
  // Leaderboard Points + KPI Points + MVP Points. Populated ONLY by the page's
  // range aggregation; day mode ranks by the SQL composite and leaves these
  // undefined (display sites fall back to points_earned).
  kpi_points?: number | null;
  mvp_points?: number | null;
  prestige_total?: number | null;
}

// P0.8-Receipts: per-cell receipts for leaderboard transparency.
// Returned by useLeaderboardReceipts; consumed by ReceiptsPopover + ReceiptsModal.
export type LeaderboardMetric =
  | "cash"
  | "show_rate"
  | "cpc"
  | "calls_booked"
  | "daily_dials"
  | "connect_to_book"
  | "score"
  | "points";

export interface Receipt {
  /** Stable key for React lists (e.g. transaction.id, dial_sessions.id, daily_leaderboard composite). */
  id: string;
  /** Display label — lead name OR date OR rank (depends on metric). */
  label: string;
  /** Numeric / status / formula text shown in the value column. */
  value: string;
  /** Date string in org tz, pre-formatted YYYY-MM-DD; component renders via formatDateShort. */
  date: string;
  /** Optional sub-label (e.g. "PIF" for cash, "no_show" for shows). Rendered muted below value. */
  sublabel?: string;
  /** For CPC split view — "cash" vs "show". Other metrics omit. */
  side?: "cash" | "show";
}

export interface ReceiptsByMetric {
  cash?: Receipt[];
  show_rate?: Receipt[];
  cpc?: Receipt[];
  calls_booked?: Receipt[];
  daily_dials?: Receipt[];
  connect_to_book?: Receipt[];
  score?: Receipt[];
  points?: Receipt[];
}

/** Keyed by rep_id — one entry per rep with all available metrics populated. */
export type LeaderboardReceiptsMap = Record<string, ReceiptsByMetric>;

export interface TeamMember {
  user_id: string;
  user_name: string;
  user_email: string;
  organization_id: string;
  organization_name: string;
  role: string;
  pod_id: string | null;
  status: string;
  ghl_user_id?: string | null;
}

// --- Confidential Data (Base Tables) ---

export interface ClientTask {
  id: string;
  organization_id: string | null; // DB allows NULL
  title: string;
  description?: string;
  status: "pending" | "in_progress" | "completed" | "cancelled"; // DB CHECK constraint: client_tasks_status_check
  due_date: string | null; // DB allows NULL
  assigned_to?: string; // uuid FK → profiles
  created_at?: string;
  profiles?: { full_name: string } | null;
  // Apr 6 schema additions (082303f)
  priority?: "low" | "normal" | "high" | "urgent" | null;
  start_date?: string | null; // date column, ISO string from PostgREST
  parent_task_id?: string | null; // uuid self-FK for subtasks
  created_by?: string | null; // uuid FK → profiles
  updated_at?: string; // timestamptz NOT NULL with default
  position?: number | null; // integer NOT NULL with default — null only when not SELECTed
  metadata?: Record<string, unknown> | null; // jsonb NOT NULL with default '{}' — null only when not SELECTed
  // Virtual field: count of associated comments (injected from PostgREST nested count)
  comment_count?: number;
  // Kanban sections (Apr 24 addition)
  section_id?: string | null; // uuid FK → client_task_sections
  // Phase D additions (W1.A migration)
  is_archived?: boolean; // soft-delete; DEFAULT false
  // G2 additions (Jun 4 2026)
  is_draft?: boolean; // mute flag; DEFAULT false — draft tasks send no Slack ping until published
}

export interface ClientTaskSection {
  id: string;
  organization_id: string;
  name: string;
  position: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientTaskComment {
  id: string;
  task_id: string;
  organization_id: string;
  author_id: string | null; // uuid FK → profiles, nullable in DB
  body: string;
  metadata?: Record<string, unknown>; // jsonb NOT NULL with default '{}'
  created_at: string;
  updated_at: string;
}

// Phase D: versioning, activity, tags (W1.A migration + new tables)

export interface ClientTaskVersion {
  id: string;
  task_id: string;
  organization_id: string;
  edited_by: string;
  edited_at: string;
  prev_description: string | null;
  new_description: string | null;
  words_added: number;
  words_removed: number;
}

export type ClientTaskActivityAction =
  | "created"
  | "title_changed"
  | "description_changed"
  | "due_changed"
  | "status_changed"
  | "priority_changed"
  | "assignee_changed"
  | "tags_added"
  | "tags_removed"
  | "section_moved"
  | "subtask_added"
  | "subtask_completed"
  | "subtask_deleted"
  | "attachment_added"
  | "attachment_removed"
  | "comment_posted"
  | "comment_edited"
  | "comment_deleted"
  | "archived"
  | "unarchived";

export interface ClientTaskActivity {
  id: string;
  task_id: string;
  organization_id: string;
  actor_id: string;
  action: ClientTaskActivityAction;
  field_name: string | null;
  old_value: unknown | null;
  new_value: unknown | null;
  metadata: Record<string, unknown>;
  occurred_at: string;
}

export interface ClientTaskTag {
  id: string;
  organization_id: string;
  name: string;
  color: string;
  created_at: string;
}

export interface ClientTaskTagAssignment {
  task_id: string;
  tag_id: string;
}

export interface ClientFile {
  id: string;
  organization_id: string;
  file_name: string;
  file_type: string;
  s3_url: string;
  shared_at: string;
  uploaded_by?: string;
  profiles?: { full_name: string } | null;
  // Phase D additions — live schema columns + W1.A migration
  task_id?: string | null; // uuid FK → client_tasks
  file_size?: number | null; // bigint in DB
  description?: string | null;
  metadata?: Record<string, unknown> | null; // jsonb in DB
  updated_at?: string; // added by W1.A migration
}

export interface ClientCommunication {
  id: string;
  organization_id: string;
  communication_type: "Zoom" | "Email" | "Slack" | "Other";
  notes: string;
  link?: string;
  created_at: string;
}

// --- Analytics Uploads ---

export interface AnalyticsUpload {
  id: string;
  organization_id: string;
  file_name: string;
  description?: string;
  upload_type: "calendly" | "webinar";
  file_content: string;
  created_at: string;
}

// --- RBAC ---

export type UserRole = "exec" | "client" | "manager" | "closer" | "setter" | "dialer" | "viewer";

// --- Role-Based View Configuration ---
// Drives dashboard composition, sidebar navigation, and route access.
// Single source of truth: config/roleConfig.ts

export type DashboardSection =
  | "hero"
  | "funnelHealth"
  | "ticketBreakdown"
  | "financialGraph"
  | "kpiGrid"
  | "salesPerformance"
  | "conversionQuality"
  | "pipelineOutreach"
  | "dmHealth" // DM engagement metrics (real convos, engagement rate, response rate)
  | "installments"
  | "leaderboard"
  | "leaderboardCloser"
  | "leaderboardSetter"
  | "leaderboardDialer"
  | "transactions"
  | "team"
  | "confidential"
  | "clientStats"
  | "closerStats"
  | "setterStats"
  | "dialerStats"
  | "sufferingLeads"
  | "upcomingCalls"
  | "perfSetter"
  | "perfCloser"
  | "perfDialer";

export type SidebarSection = "portal" | "management" | "analytics" | "execTools";

export interface RoleView {
  /** Which sidebar section groups are visible */
  sidebar: SidebarSection[];
  /** Which dashboard sections render, in order */
  dashboard: DashboardSection[];
  /** Which route paths this role can access (used by RouteGuard) */
  pages: string[];
}

export interface Organization {
  id: string;
  name: string;
  slug: string | null;
  status: string;
  timezone: string | null; // DB allows NULL, default 'UTC' - use COALESCE in queries
  settings: Record<string, any> | null;
  slack_team_id: string | null;
  google_drive_url?: string | null;
  created_at: string;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: UserRole;
  orgRoles: Record<string, UserRole>;
  podId?: string;
  managedPodIds?: string[]; // Pod IDs this user manages — set at login for manager role
  sector?: string;
  ghlUserId?: string; // GHL user ID for sending messages
  isSuperAdmin?: boolean; // profiles.is_super_admin — Mako + Nathaniel only (GOD tier). Gates SOP editing.
  displayCurrency?: string | null; // profiles.display_currency (ISO code). Display-only; drives the currency toggle. null = USD.
}

// --- Lead Timeline (from get_lead_timeline RPC) ---

export interface LeadTimelineLead {
  name: string | null;
  email: string;
  phone: string | null;
  instagram: string | null;
  timezone: string | null;
  status: string | null;
}

// Step 6 (lead-phone-normalization plan): one lead's resolved identity, reported
// UP from LeadTimelinePanel so LeadCompareHeader can align two leads side-by-side.
// The get_suggested_lead_matches RPC omits instagram, so each panel self-reports
// its own IG here rather than the header reading it off the match seed.
export interface LeadCompareIdentity {
  email: string | null;
  name: string | null;
  phone: string | null;
  instagram: string | null;
}

export interface LeadTimelineEvent {
  event_date: string | null;
  event_type: string;
  status: string;
  cash_collected: number;
  revenue_closed: number;
  closer_name: string | null;
  setter_name: string | null;
  setter_id: string | null;
  setter_role: UserRole | null;
  sales_program_sold: string | null;
  call_start_time: string | null;
  call_booked_time: string | null;
  call_cancel_by: string | null;
  call_cancel_reason: string | null;
  sales_next_payment: string | null;
  sales_next_payment_amount: number | null;
  custom_data: Record<string, any> | null;
  web_signup_source: string | null;
  sort_rank: number;
}

export interface LeadTimelineResult {
  lead: LeadTimelineLead;
  timeline: LeadTimelineEvent[];
}

// --- Prestige ---

export interface PrestigeEntry {
  rep_id: string;
  rep_name: string;
  rep_email: string;
  organization_id: string;
  organization_name: string;
  pod_id: string;
  pod_name: string;
  sector: string;
  month: string;
  total_points: number;
  rank_in_organization: number | null;
  rank_in_pod: number | null;
  rank_in_sector: number | null;
  points_history: Record<string, number>;
  created_at: string;
  updated_at: string;
  // Exposed on prestige_points_readable 2026-07-03 (P0-LBTOTAL) — the view
  // previously stripped both; reading them via `?? 0` before that date
  // silently returned 0.
  kpi_points?: number | null;
  mvp_points?: number | null;
}

export interface PrestigeRank {
  rep_id: string;
  organization_id: string;
  pod_id: string | null;
  sector: string;
  current_level: number;
  highest_level_achieved: number;
  consecutive_non_wins: number;
  last_win_month: string | null;
  rep_name?: string;
  avatar_url?: string;
  pod_name?: string;
}

export interface PrestigeHistoryEntry {
  month: string;
  event_type: "rank_up" | "de_rank" | "buffer_used" | "no_change";
  old_level: number;
  new_level: number;
  total_points: number | null;
  leaderboard_points: number | null;
  kpi_points: number | null;
  mvp_points: number | null;
  was_winner: boolean;
  bonus_earned: number;
}

export interface PrestigeProfile {
  rep_id: string;
  rep_name: string;
  avatar_url: string | null;
  sector: string | null;
  pod_name: string | null;
  current_level: number;
  highest_level_achieved: number;
  consecutive_non_wins: number;
  last_win_month: string | null;
  current_month: {
    month: string;
    total_points: number;
    leaderboard_points: number;
    kpi_points: number;
    mvp_points: number;
    rank_in_pod: number | null;
    rank_in_sector: number | null;
    rank_in_organization: number | null;
    days_left: number;
  };
  history: PrestigeHistoryEntry[];
  total_wins: number;
  total_bonuses_earned: number;
  permanent_emblems: number[];
}

export interface MvpVote {
  id: string;
  organization_id: string;
  month: string;
  voter_id: string;
  nominee_id: string;
  created_at: string;
}

// --- AI Summaries ---

export interface GHLAISummary {
  id: string;
  conversation_id: string;
  organization_id: string;
  summary_type: "chunk" | "meta";
  summary_text: string;
  message_range_start: number | null;
  message_range_end: number | null;
  model_used: string | null;
  created_at: string;
}

// --- Setter Proof & Transparency ---

export type ProofMetricType =
  | "engaged_response"
  | "work_sessions"
  | "session_gap"
  | "ignored_per_session"
  | "lead_reply_time"
  | "reply_turn_distribution"
  | "avg_reply_time"
  | "calls_pitched"
  | "followup_persistence"
  | "conversation_span"
  | "unengaged_conversations"
  | "speed_to_lead"
  | "unreplied";

export interface EngagedResponseProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  lead_msg_at: string;
  setter_reply_at: string;
  response_mins: number;
  bucket: "<5min" | "5-15min" | "15min+";
}

export interface WorkSessionProof {
  setter_id: string;
  setter_name: string;
  session_number: number;
  session_start: string;
  session_end: string;
  duration_mins: number;
  conversations_handled: number;
  messages_sent: number;
}

export interface SessionGapProof {
  setter_id: string;
  setter_name: string;
  previous_session_end: string;
  next_session_start: string;
  gap_mins: number;
  bucket: "<30min" | "30min-1hr" | "1-2hr" | "2hr+";
}

export interface IgnoredPerSessionProof {
  setter_id: string;
  setter_name: string;
  session_number: number;
  session_start: string;
  session_end: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  lead_msg_at: string;
  was_replied: boolean;
}

export interface LeadReplyTimeProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  setter_msg_at: string;
  lead_reply_at: string;
  reply_mins: number;
  bucket: "<5min" | "5-30min" | "30min-2hr" | "2hr+";
}

export interface ReplyTurnDistributionProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  reply_turns: number;
  bucket: "ghost" | "light" | "engaged" | "deep";
}

export interface AvgReplyTimeProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  setter_msg_at: string;
  lead_reply_at: string;
  reply_mins: number;
  bucket: "<30min" | "30min-2hr" | "2hr+" | "ignored";
}

export interface SpeedToLeadProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  lead_msg_at: string;
  setter_reply_at: string | null;
  response_mins: number | null;
  bucket: "<5min" | "5-30min" | "30min-2hr" | "2hr+" | "pending" | "no_reply";
}

export interface CallsPitchedProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  calendly_msg_at: string;
  message_text: string;
  got_reply: boolean;
  bucket: "replied" | "no_reply";
}

export interface FollowupPersistenceProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  followup_days: number;
  first_followup_date: string;
  last_followup_date: string;
  bucket: string;
}

export interface ConversationSpanProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  span_days: number;
  total_msgs: number;
  bucket: "same_day" | "1-2d" | "3-6d" | "1-2w" | "2w+";
}

export interface UnengagedConversationProof {
  conversation_id: string;
  contact_name: string;
  lead_instagram: string | null;
  first_lead_msg_date: string; // YYYY-MM-DD
  last_lead_msg_at: string | null; // ISO timestamp
  last_message_text: string;
  days_since_lead_first_reached_out: number;
  total_count: number; // window total — same on every row, used for pagination
}

export interface UnrepliedProof {
  setter_id: string;
  setter_name: string;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  last_lead_msg_at: string;
  bucket: "0_4h" | "4_24h" | "24_48h" | "48h_plus";
}

export type ProofDataMap = {
  engaged_response: EngagedResponseProof;
  work_sessions: WorkSessionProof;
  session_gap: SessionGapProof;
  ignored_per_session: IgnoredPerSessionProof;
  lead_reply_time: LeadReplyTimeProof;
  reply_turn_distribution: ReplyTurnDistributionProof;
  avg_reply_time: AvgReplyTimeProof;
  calls_pitched: CallsPitchedProof;
  followup_persistence: FollowupPersistenceProof;
  conversation_span: ConversationSpanProof;
  unengaged_conversations: UnengagedConversationProof;
  speed_to_lead: SpeedToLeadProof;
  unreplied: UnrepliedProof;
};

// --- Revenue Planner ---

export interface MonthlyRevenueGoal {
  id: string;
  organization_id: string;
  month: string; // ISO date string (first of month)
  revenue_goal: number;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// --- ManyChat Detection ---

export interface MessagePattern {
  id: string;
  organization_id: string;
  message_text: string;
  message_hash: string;
  occurrence_count: number;
  classification: "manychat" | "human_script" | "unclassified" | null; // DB constraint requires 'human_script', not 'manual'
  classified_at: string | null;
  classified_by: string | null;
  sample_conversation_ids: string[];
  first_seen_at: string | null;
  last_seen_at: string | null;
  created_at: string;
}

// --- Pre-Call Compliance ---

export interface PrecallOutcomeMatch {
  id: string;
  precall_transaction_id: string;
  outcome_transaction_id: string;
  organization_id: string;
  match_score: number;
  match_reasons: Array<{
    type: string;
    score: number;
    details?: string;
  }>;
  status: "auto_matched" | "pending_review" | "confirmed" | "rejected";
  matched_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}

// --- Lead Identity Matching ---
// Used by deduplication system to track potential duplicate leads across the org.
// Match reasons stored as JSONB with scoring breakdown.

export interface LeadIdentityMatch {
  id: string;
  lead_id_a: string;
  lead_id_b: string;
  organization_id: string;
  match_score: number;
  match_reasons: Record<string, any>; // JSONB: { email_match: true, phone_match: false, ... }
  status: "auto_merged" | "pending_review" | "confirmed" | "rejected";
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}

// --- Setter Daily Performance ---
// Pre-aggregated setter metrics per day. Composite PK: (organization_id, setter_id, work_date).
// Used by get_setter_performance_fast RPC and DM Analytics dashboard.
// Arrays store raw values for percentile/histogram calculations.

export interface SetterDailyPerformance {
  // Primary key columns
  organization_id: string;
  setter_id: string; // TEXT - maps to ghl_users.ghl_user_id
  work_date: string; // DATE as ISO string

  // Conversation counts
  total_conversations: number | null;
  real_conversations: number | null; // Deprecated metric. Old 2+/2+ threshold. Use deepConversations (4+ setter reply turns) for canonical definition.
  total_messages_sent: number | null;

  // Engaged Response (reply to active conversation)
  engaged_response_sum: number | null;
  engaged_response_count: number | null;
  engaged_response_values: number[] | null;

  // Session Gaps (breaks between work sessions)
  session_gap_sum: number | null;
  session_gap_count: number | null;
  session_gap_values: number[] | null;

  // Work Sessions
  session_count: number | null;
  total_work_minutes: number | null;

  // Lead Reply Time (how fast leads reply to setter)
  lead_reply_sum: number | null;
  lead_reply_count: number | null;
  lead_reply_values: number[] | null;

  // Average Reply Time
  avg_reply_time_sum: number | null;
  avg_reply_time_count: number | null;
  avg_reply_time_values: number[] | null;

  // Sentiment tracking
  positive_sentiment_count: number | null;
  negative_sentiment_count: number | null;
  neutral_sentiment_count: number | null;

  // Ignored conversations (no reply from setter)
  total_ignored_conversations: number | null;
  total_ignore_events: number | null;

  // Work window tracking (minute of day, 0-1439)
  first_msg_minute_of_day: number | null;
  last_msg_minute_of_day: number | null;
  active_window_mins: number | null;
  work_mins: number | null;
  break_mins: number | null;

  // Reply turn distribution (conversation depth buckets)
  total_reply_turns: number | null;
  reply_turn_ghost: number | null; // 0-1 turns
  reply_turn_light: number | null; // 2-3 turns
  reply_turn_engaged: number | null; // 4-6 turns
  reply_turn_deep: number | null; // 7+ turns

  // Timestamps
  created_at: string | null;
  updated_at: string | null;
}

// ============================================================================
// YearOverview — Year View Tab on /admin/dashboard (P9/P10/P11, [doing]_SANKEY_FUNNEL)
// ============================================================================
// Returned by get_year_overview(p_organization_id, p_year, p_day_start?, p_day_end?) RPC.
// `goal_amount` is NULL when monthly_revenue_goals has no row for that month OR when
// the call is sliced (p_day_start/p_day_end != defaults). `slice_label` is 'Days N–M'
// (en-dash U+2013) when sliced, NULL when defaults. `slice_start_day`/`slice_end_day`
// echo the params (default 1, 31). All cash/revenue values are direct from transactions
// (P10) — anchored to call_start_time for call-type events, event_date otherwise.
export interface YearMonthData {
  month_num: number; // 1-12
  month_start: string; // 'YYYY-MM-01'
  cash_collected: number;
  revenue_closed: number;
  deals_closed: number;
  high_ticket_cash: number;
  low_ticket_cash: number;
  installments_collected: number;
  daily: { date: string; cash: number; revenue: number }[]; // date = 'MM/DD'
  goal_amount: number | null;
  days_in_month: number;
  days_elapsed: number;
  is_completed: boolean;
  is_current: boolean;
  slice_label: string | null; // 'Days 1–5' when sliced, NULL when default 1-31
}

export interface YearOverview {
  year: number;
  timezone: string;
  today: string;
  slice_start_day: number; // echoes p_day_start; default 1
  slice_end_day: number; // echoes p_day_end; default 31
  months: YearMonthData[];
}
